import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, MapPin, Monitor, Users, Mail, BarChart3, ChevronDown } from 'lucide-react';
const stages = [{
  icon: Target,
  phase: 'Pre-Congress',
  weeks: '4–6 weeks before',
  channel: 'Email, WhatsApp, Social, QR on invites',
  snak: 'Peer Poll Snak™',
  description: 'Distribute a teaser Snak™ asking "When do you escalate beyond RAAS blockade?" via email and WhatsApp. HCPs answer, see peer benchmarks, and receive a CTA to visit the CSL booth for the full data.',
  color: '#E60012',
  image: 'https://cdn.magicpatterns.com/uploads/mM3VD4xQ8xTXdj5TvCmH3g/Screenshot_2026-04-01_at_09.02.47.png'
}, {
  icon: MapPin,
  phase: 'Vicinity',
  weeks: 'Day of congress',
  channel: 'Geo-targeted ads, transit QR codes',
  snak: 'Quiz Challenge Snak™',
  description: 'Geo-targeted ads in ride-share apps near the venue serve a quick IgA Nephropathy quiz. Correct answers unlock a booth visit incentive with directions to CSL Booth #215.',
  color: '#C5000F',
  image: 'https://cdn.magicpatterns.com/uploads/8zbPoGeL8TMu9dqeqAr2Hr/Screenshot_2026-02-11_at_18.52.54.png'
}, {
  icon: Monitor,
  phase: 'Aisle',
  weeks: 'During congress',
  channel: 'iPads, touchscreens, QR on booth exterior',
  snak: 'Self-Guided Explorer Snak™',
  description: 'Stationary iPads outside the booth perimeter let HCPs browse disease areas (IgA Nephropathy, HAE, hemophilia) at their own pace. Menu Cue™ lets them choose their topic. No rep interaction required — lowering the barrier to engage.',
  color: '#E60012',
  image: 'https://cdn.magicpatterns.com/uploads/jtmDZhu5YvVkqSvWyA7DSo/Screenshot_2026-02-11_at_19.08.09.png'
}, {
  icon: Users,
  phase: 'Booth',
  weeks: 'During congress',
  channel: 'Rep-led iPads',
  snak: 'Deep-Dive Conversation Snak™',
  description: 'Once inside the booth, reps use guided Snaks™ to walk HCPs through clinical data, capture specialty and interest via Input Cues, and collect consent for follow-up — all within a single seamless flow.',
  color: '#C5000F',
  image: 'https://cdn.magicpatterns.com/uploads/mqGiMzwaC8ySRv1TPEQBU1/Screenshot_2026-02-11_at_18.10.17.png'
}, {
  icon: Mail,
  phase: 'Post-Congress',
  weeks: '1–2 weeks after',
  channel: 'Email, WhatsApp',
  snak: 'Follow-Up Recap Snak™',
  description: 'Opted-in HCPs receive a personalized "Thank You" Snak™ with session highlights, downloadable clinical summaries, and a Rating Cue™ to capture feedback. Data feeds into CRM for rep follow-up.',
  color: '#E60012',
  image: 'https://cdn.magicpatterns.com/uploads/1yzsnj6wqDNUHRV1oyVyfh/Screenshot_2026-03-24_at_08.34.05.png'
}];
export function UseCasePage() {
  return <div className="min-h-screen bg-[#FDF8F8] font-sans text-[#2D2926] selection:bg-[#E60012] selection:text-white" data-id="element-777">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full z-50 px-6 py-4 flex justify-between items-center bg-white/80 backdrop-blur-md border-b border-gray-100" data-id="element-778">
        <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-[#E60012] transition-colors font-medium text-sm" data-id="element-779">
          <ArrowLeft className="w-4 h-4" data-id="element-780" />
          Back to Presentation
        </Link>
        <div className="flex items-center gap-3" data-id="element-781">
          <div className="bg-white rounded-full px-3 py-1.5 shadow-sm border border-gray-100" data-id="element-782">
            <img src="https://cdn.magicpatterns.com/uploads/6UmWqd3TWdNZzkJ6AZQjM5/image.png" alt="Snakker logo" className="h-4 w-auto object-contain" data-id="element-783" />
          </div>
          <div className="bg-white rounded-full px-3 py-1.5 shadow-sm border border-gray-100" data-id="element-784">
            <img src="https://cdn.magicpatterns.com/uploads/bZj2MmfA3zaGt5x716Y5zP/Screenshot_2026-03-31_at_16.01.46.png" alt="CSL logo" className="h-5 w-auto object-contain" data-id="element-785" />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-20 pb-12 overflow-hidden bg-[#1A1A2E] text-white" data-id="element-786">
        {/* Abstract background elements */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#E60012] rounded-full blur-[150px] opacity-20 translate-x-1/3 -translate-y-1/3" data-id="element-787" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#C5000F] rounded-full blur-[120px] opacity-20 -translate-x-1/3 translate-y-1/3" data-id="element-788" />

        <div className="max-w-6xl mx-auto w-full relative z-10" data-id="element-789">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-4xl" data-id="element-790">
            <div className="flex items-center gap-3 mb-6" data-id="element-791">
              <div className="w-12 h-[2px] bg-[#E60012]" data-id="element-792" />
              <span className="text-[#E60012] font-bold tracking-widest uppercase text-sm" data-id="element-793">
                Use Case
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6" data-id="element-794">
              CSL × Snakker™ <br data-id="element-795" />
              <span className="text-white/60" data-id="element-796">at ERA Congress</span>
            </h1>

            <p className="text-xl md:text-2xl text-white/80 font-light leading-relaxed mb-12 max-w-3xl" data-id="element-797">
              Full-funnel activation for IgA Nephropathy awareness. Lowering
              barriers to engagement from pre-congress to post-congress
              follow-up.
            </p>

            {/* Metadata Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-8 border-t border-white/10" data-id="element-798">
              <div data-id="element-799">
                <span className="text-white/40 text-xs uppercase tracking-widest block mb-2" data-id="element-800">
                  Client
                </span>
                <p className="font-medium text-lg" data-id="element-801">CSL Vifor</p>
              </div>
              <div data-id="element-802">
                <span className="text-white/40 text-xs uppercase tracking-widest block mb-2" data-id="element-803">
                  Congress
                </span>
                <p className="font-medium text-lg" data-id="element-804">ERA 2026</p>
              </div>
              <div data-id="element-805">
                <span className="text-white/40 text-xs uppercase tracking-widest block mb-2" data-id="element-806">
                  Disease Area
                </span>
                <p className="font-medium text-lg" data-id="element-807">IgA Nephropathy</p>
              </div>
              <div data-id="element-808">
                <span className="text-white/40 text-xs uppercase tracking-widest block mb-2" data-id="element-809">
                  Audience
                </span>
                <p className="font-medium text-lg" data-id="element-810">Nephrologists, GPs</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1,
        duration: 1
      }} data-id="element-811">
          <span className="text-xs uppercase tracking-widest" data-id="element-812">
            Scroll to explore
          </span>
          <motion.div animate={{
          y: [0, 8, 0]
        }} transition={{
          repeat: Infinity,
          duration: 2,
          ease: 'easeInOut'
        }} data-id="element-813">
            <ChevronDown className="w-5 h-5" data-id="element-814" />
          </motion.div>
        </motion.div>
      </section>

      {/* Objective Section */}
      <section className="py-24 px-6 bg-white" data-id="element-815">
        <div className="max-w-4xl mx-auto" data-id="element-816">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} className="flex flex-col md:flex-row gap-12 items-start" data-id="element-817">
            <div className="md:w-1/3 shrink-0" data-id="element-818">
              <div className="w-16 h-16 rounded-2xl bg-[#E60012]/10 flex items-center justify-center mb-6" data-id="element-819">
                <BarChart3 className="w-8 h-8 text-[#E60012]" data-id="element-820" />
              </div>
              <h2 className="text-3xl font-bold" data-id="element-821">The Objective</h2>
            </div>
            <div className="md:w-2/3" data-id="element-822">
              <p className="text-xl text-gray-600 leading-relaxed" data-id="element-823">
                Activate the full congress journey — from setting the clinical
                agenda weeks before ERA, through vicinity nudging and aisle
                engagement, to deep booth conversations and post-congress
                follow-up. Each touchpoint uses a tailored Snak™ to lower
                barriers and move HCPs closer to meaningful engagement with
                CSL's IgA Nephropathy data.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stages Pipeline */}
      <div className="bg-[#FDF8F8] py-24" data-id="element-824">
        <div className="max-w-7xl mx-auto px-6" data-id="element-825">
          <div className="text-center mb-24" data-id="element-826">
            <h2 className="text-4xl font-bold mb-4" data-id="element-827">The Snak™ Journey</h2>
            <p className="text-xl text-gray-500" data-id="element-828">
              5 stages of continuous engagement
            </p>
          </div>

          <div className="space-y-32" data-id="element-829">
            {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isEven = index % 2 === 0;
            return <motion.section key={stage.phase} initial={{
              opacity: 0,
              y: 50
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8
            }} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-16 items-center`} data-id="element-830">
                  {/* Content */}
                  <div className="w-full md:w-1/2" data-id="element-831">
                    <div className="flex items-center gap-4 mb-6" data-id="element-832">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{
                    backgroundColor: stage.color
                  }} data-id="element-833">
                        <Icon className="w-8 h-8" data-id="element-834" />
                      </div>
                      <div data-id="element-835">
                        <span className="text-[#E60012] font-bold tracking-widest uppercase text-sm block mb-1" data-id="element-836">
                          Stage 0{index + 1}
                        </span>
                        <h3 className="text-4xl font-bold" data-id="element-837">{stage.phase}</h3>
                      </div>
                    </div>

                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 mb-8" data-id="element-838">
                      <div className="flex flex-wrap gap-3 mb-6" data-id="element-839">
                        <div className="bg-[#E60012]/10 text-[#E60012] px-4 py-2 rounded-full text-sm font-bold" data-id="element-840">
                          {stage.snak}
                        </div>
                        <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-full text-sm font-medium" data-id="element-841">
                          {stage.channel}
                        </div>
                        <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-full text-sm font-medium" data-id="element-842">
                          {stage.weeks}
                        </div>
                      </div>

                      <p className="text-xl text-gray-600 leading-relaxed" data-id="element-843">
                        {stage.description}
                      </p>
                    </div>
                  </div>

                  {/* Visual */}
                  <div className="w-full md:w-1/2" data-id="element-844">
                    <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl" data-id="element-845">
                      <div className="absolute inset-0 bg-black/10 z-10" data-id="element-846" />
                      <img src={stage.image} alt={`${stage.phase} visual`} className="w-full h-full object-cover" data-id="element-847" />
                      {/* Decorative elements */}
                      <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#E60012] rounded-full blur-[40px] opacity-20" data-id="element-848" />
                      <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#C5000F] rounded-full blur-[40px] opacity-20" data-id="element-849" />
                    </div>
                  </div>
                </motion.section>;
          })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1A1A2E] text-white py-12 px-6 border-t border-white/10" data-id="element-850">
        <div className="max-w-4xl mx-auto text-center" data-id="element-851">
          <div className="flex justify-center gap-4 mb-8" data-id="element-852">
            <div className="bg-white/10 rounded-full px-4 py-2" data-id="element-853">
              <img src="https://cdn.magicpatterns.com/uploads/6UmWqd3TWdNZzkJ6AZQjM5/image.png" alt="Snakker logo" className="h-5 w-auto object-contain brightness-0 invert" data-id="element-854" />
            </div>
            <div className="bg-white/10 rounded-full px-4 py-2" data-id="element-855">
              <img src="https://cdn.magicpatterns.com/uploads/bZj2MmfA3zaGt5x716Y5zP/Screenshot_2026-03-31_at_16.01.46.png" alt="CSL logo" className="h-6 w-auto object-contain brightness-0 invert" data-id="element-856" />
            </div>
          </div>
          <p className="text-sm text-white/40 leading-relaxed max-w-2xl mx-auto" data-id="element-857">
            This use case describes a proposed Snakker™ activation for CSL at
            ERA Congress. All Snak™ content is subject to medical-legal review.
          </p>
        </div>
      </footer>
    </div>;
}